import React, { useState, useEffect } from "react";
import {
  X,
  DollarSign,
  Calendar,
  FileText,
  Info,
  CheckCircle,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import moment from "moment";

export default function BossStatisticPlacesModalAddExtraProfit({
  visible,
  onClose,
  onSuccess,
  placeId,
  placeName,
}) {
  const [userData, setUserData] = useState(null);
  const [profit, setProfit] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(moment().format("DD-MM-YYYY"));
  const [loading, setLoading] = useState(false);

  // Load userData from localStorage
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) setUserData(JSON.parse(user));
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      setProfit("");
      setNote("");
      setDate(moment().format("DD-MM-YYYY"));
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (!profit || profit.trim() === "") {
      alert("Please enter profit amount");
      return;
    }
    const profitValue = parseFloat(profit);
    if (isNaN(profitValue) || profitValue <= 0) {
      alert("Please enter a valid profit amount");
      return;
    }
    if (!userData) {
      alert("User data not found");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${MyDomain}PlaceExtraProfit/CreatePlaceExtraProfit`, {
        UserId: userData.id,
        PlaceId: placeId,
        Profit: Math.round(profitValue),
        Date: date,
        Note: note.trim() || null,
      });

      alert("Extra profit added successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("❌ Error creating extra profit:", error);
      alert("Failed to add extra profit");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-green-500" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900">
                Add Extra Profit
              </h2>
              <p className="text-sm text-gray-500 truncate">{placeName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition flex-shrink-0 ml-3"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* ── Form ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Date — native date picker */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Date <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3 bg-gray-50 border border-green-200 rounded-xl px-4 py-3 focus-within:border-green-400 transition">
              <Calendar className="w-5 h-5 text-green-500 flex-shrink-0" />
              <input
                type="date"
                value={
                  // Convert DD-MM-YYYY → YYYY-MM-DD for the native date input
                  date ? moment(date, "DD-MM-YYYY").format("YYYY-MM-DD") : ""
                }
                onChange={(e) => {
                  // Convert YYYY-MM-DD back to DD-MM-YYYY
                  if (e.target.value) {
                    setDate(
                      moment(e.target.value, "YYYY-MM-DD").format("DD-MM-YYYY"),
                    );
                  }
                }}
                className="flex-1 bg-transparent outline-none text-gray-900 font-semibold"
              />
            </div>
          </div>

          {/* Profit */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Extra Profit <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center bg-gray-50 border border-green-200 rounded-xl px-4 py-3 focus-within:border-green-400 transition">
              <DollarSign className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <input
                type="number"
                value={profit}
                onChange={(e) => setProfit(e.target.value)}
                placeholder="Enter amount"
                className="flex-1 bg-transparent outline-none text-gray-900 font-semibold text-base"
              />
              <span className="text-green-500 font-bold ml-2">₪</span>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Note <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-gray-400 transition">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add note about extra work"
                rows={4}
                className="flex-1 bg-transparent outline-none text-gray-900 resize-none text-sm"
              />
            </div>
          </div>

          {/* Info box */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800 leading-relaxed">
              Extra profit is additional income earned beyond the regular
              payment for this place. It will be added to the net profit
              calculation for the selected month.
            </p>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3.5 rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold transition ${
              loading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600 text-white shadow-md"
            }`}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Adding...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Add Extra Profit</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin   { animation: spin 1s linear infinite; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
